// File: client/src/components/rules/NodeConfigModal.js
import React, { useState, useEffect } from 'react';
import {
    Modal,
    Tabs,
    Form,
    Input,
    Button,
    Typography
} from 'antd';

// Import configuration components for different node types
import TriggerConfig from './nodeConfigs/TriggerConfig';
import ConditionalConfig from './nodeConfigs/ConditionalConfig';
import SwitchConfig from './nodeConfigs/SwitchConfig';
import EmailConfig from './nodeConfigs/EmailConfig';
import WebhookConfig from './nodeConfigs/WebhookConfig';
import UpdateRecordConfig from './nodeConfigs/UpdateRecordConfig';
import CompleteConfig from './nodeConfigs/CompleteConfig';
import ErrorConfig from './nodeConfigs/ErrorConfig';

const { TabPane } = Tabs;
const { Text } = Typography;

const NodeConfigModal = ({ visible, node, onClose, onUpdate }) => {
    const [form] = Form.useForm();
    const [activeTab, setActiveTab] = useState('general');
    const [nodeType, setNodeType] = useState('');

    // Reset form when node changes
    useEffect(() => {
        if (node) {
            const config = node.data.config || {};
            setNodeType(getNodeType(node));

            // Reset form
            form.resetFields();

            // Set form values
            form.setFieldsValue({
                label: node.data.label,
                ...config
            });

            setActiveTab('general');
        }
    }, [node, form]);

    // Get node type
    const getNodeType = (node) => {
        if (!node) return '';

        switch (node.type) {
            case 'triggerNode':
                return 'trigger';
            case 'conditionalNode':
                return 'conditional';
            case 'switchNode':
                return 'switch';
            case 'actionNode':
                return node.data.actionType || node.data.config?.type || 'action';
            case 'completeNode':
                return 'complete';
            case 'errorNode':
                return 'error';
            default:
                return '';
        }
    };

    // Handle form submission
    const handleSubmit = () => {
        form.validateFields()
            .then(values => {
                if (!node) return;

                // Update node configuration
                const updatedNode = {
                    ...node,
                    data: {
                        ...node.data,
                        label: values.label,
                        config: buildNodeConfig(values)
                    }
                };

                // For specific node types, update additional data
                switch (nodeType) {
                    case 'conditional':
                        updatedNode.data.conditionType = values.type;
                        break;
                    case 'switch':
                        updatedNode.data.switchType = values.type;
                        updatedNode.data.outputs = values.outputs || ['Option 1', 'Option 2', 'Default'];
                        break;
                    case 'trigger':
                        updatedNode.data.triggerType = values.type;
                        break;
                    case 'email':
                    case 'webhook':
                    case 'updateRecord':
                        updatedNode.data.actionType = nodeType;
                        break;
                }

                // Update the node
                onUpdate(updatedNode);
            })
            .catch(errorInfo => {
                console.error('Validation failed:', errorInfo);
            });
    };

    // Build node configuration based on node type
    const buildNodeConfig = (values) => {
        const { label, ...configValues } = values;

        // Base configuration
        const config = {
            ...configValues
        };

        return config;
    };

    // Render configuration form based on node type
    const renderConfigForm = () => {
        if (!node) return null;

        switch (nodeType) {
            case 'trigger':
                return <TriggerConfig />;
            case 'conditional':
                return <ConditionalConfig />;
            case 'switch':
                return <SwitchConfig />;
            case 'email':
                return <EmailConfig />;
            case 'webhook':
                return <WebhookConfig />;
            case 'updateRecord':
                return <UpdateRecordConfig />;
            case 'complete':
                return <CompleteConfig />;
            case 'error':
                return <ErrorConfig />;
            default:
                return <Text>Configuration not available for this node type.</Text>;
        }
    };

    return (
        <Modal
            title={`Configure ${node?.data?.label || 'Node'}`}
            open={visible}
            onCancel={onClose}
            width={800}
            footer={[
                <Button key="cancel" onClick={onClose}>
                    Cancel
                </Button>,
                <Button key="submit" type="primary" onClick={handleSubmit}>
                    Save
                </Button>
            ]}
        >
            {node ? (
                <Form
                    form={form}
                    layout="vertical"
                    initialValues={{
                        label: node.data?.label,
                        ...node.data?.config
                    }}
                >

                            <Form.Item
                                name="label"
                                label="Label"
                                rules={[{ required: true, message: 'Please enter a label' }]}
                            >
                                <Input placeholder="Node label" />
                            </Form.Item>

                            {renderConfigForm()}

                </Form>
            ) : (
                <div>No node selected</div>
            )}
        </Modal>
    );
};

export default NodeConfigModal;
